/**
 * Leviathan Web - Customer Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import CustomerForm from '@components/customers/CustomerForm';
import CustomerMargins from './CustomerMargins';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

interface Customer {
  id: string;
  name: string;
  displayName: string | null;
  customerNumber: string;
  type: string;
  status: string;
  primaryContactFirstName: string | null;
  primaryContactLastName: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  billingAddress: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string | null;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  taxId: string | null;
  industryType: string | null;
  creditLimit: number | null;
  creditTerms: number | null;
  pricingTier: string | null;
  paymentMethod: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Order {
  id: string;
  orderNumber: string;
  orderType: string;
  status: string;
  total: number;
  orderDate: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  total: number;
  amountDue: number;
  invoiceDate: string;
  dueDate: string;
}

interface CustomerLocation {
  id: string;
  name: string;
  locationCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  locationType: string | null;
  isActive: boolean;
  _count: {
    tanks: number;
    orders: number;
  };
}

interface Tank {
  id: string;
  tankNumber: string;
  name: string;
  capacity: number;
  currentLevel: number;
  status: string;
  product: {
    id: string;
    name: string;
  } | null;
  customerLocation: {
    id: string;
    name: string;
  } | null;
}

interface LocationSummary {
  id: string;
  name: string;
  locationCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  isActive: boolean;
  activeTanks: number;
  orderCount: number;
  lastDeliveryDate: string | null;
  currentMargin: {
    marginPercent: number | null;
    marginAmount: number | null;
    productName: string;
  } | null;
  revenue: number;
}

interface LocationSummaryTotals {
  totalLocations: number;
  activeLocations: number;
  totalTanks: number;
  totalOrders: number;
  totalRevenue: number;
}

type LocationSortKey = 'name' | 'currentMargin' | 'activeTanks' | 'orderCount' | 'lastDeliveryDate' | 'revenue';

type TabType = 'overview' | 'locations' | 'tanks' | 'margins' | 'orders' | 'invoices';

const CustomerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [locations, setLocations] = useState<CustomerLocation[]>([]);
  const [tanks, setTanks] = useState<Tank[]>([]);
  const [locationSummaries, setLocationSummaries] = useState<LocationSummary[]>([]);
  const [locationTotals, setLocationTotals] = useState<LocationSummaryTotals | null>(null);
  const [locationSortKey, setLocationSortKey] = useState<LocationSortKey>('name');
  const [locationSortAsc, setLocationSortAsc] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Get initial tab from URL params or default to overview
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'locations', 'tanks', 'margins', 'orders', 'invoices'].includes(tabParam)) {
      return tabParam as TabType;
    }
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);

  // Update tab from URL when it changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'locations', 'tanks', 'margins', 'orders', 'invoices'].includes(tabParam)) {
      setActiveTab(tabParam as TabType);
    }
  }, [searchParams]);

  // Handle tab change and update URL
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`/customers/${id}?tab=${tab}`, { replace: true });
  };

  const fetchCustomer = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomer(data.customer);
      } else {
        setCustomer(null);
      }
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      setCustomer(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, accessToken]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders?customerId=${id}&limit=20`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  }, [id, accessToken]);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoices?customerId=${id}&limit=20`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  }, [id, accessToken]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/customer-locations?customerId=${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, [id, accessToken]);

  const fetchTanks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tanks?customerId=${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTanks(data.tanks || []);
      }
    } catch (error) {
      console.error('Failed to fetch tanks:', error);
    }
  }, [id, accessToken]);

  const fetchLocationSummaries = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/customer-locations/customer/${id}/summary?days=90`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setLocationSummaries(data.summaries || []);
        setLocationTotals(data.totals || null);
      }
    } catch (error) {
      console.error('Failed to fetch location summaries:', error);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchCustomer();
    fetchOrders();
    fetchInvoices();
    fetchLocations();
    fetchTanks();
    fetchLocationSummaries();
  }, [fetchCustomer, fetchOrders, fetchInvoices, fetchLocations, fetchTanks, fetchLocationSummaries]);

  const handleDelete = async () => {
    setDeleteError('');
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        navigate('/customers');
      } else {
        const error = await response.json();
        setDeleteError(error.error || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Failed to delete customer:', error);
      setDeleteError('Failed to delete customer');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-success-green';
      case 'INACTIVE':
        return 'text-text-tertiary';
      case 'SUSPENDED':
        return 'text-error-red';
      case 'LEAD':
        return 'text-amber';
      default:
        return 'text-text-secondary';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'DELIVERED':
        return 'text-success-green';
      case 'PENDING':
      case 'CONFIRMED':
        return 'text-amber';
      case 'IN_PROGRESS':
      case 'DISPATCHED':
        return 'text-steel';
      case 'CANCELLED':
        return 'text-error-red';
      default:
        return 'text-text-secondary';
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'text-success-green';
      case 'PENDING':
      case 'SENT':
        return 'text-amber';
      case 'OVERDUE':
        return 'text-error-red';
      case 'VOID':
      case 'CANCELLED':
        return 'text-text-tertiary';
      default:
        return 'text-text-secondary';
    }
  };

  // Calculate stats
  const totalRevenue = orders.reduce((sum, o) => sum + o.total, 0);
  const outstandingBalance = invoices
    .filter((i) => i.status !== 'PAID')
    .reduce((sum, i) => sum + (i.amountDue || i.total), 0);

  // Sorted location summaries
  const sortedLocationSummaries = useMemo(() => {
    const sorted = [...locationSummaries];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (locationSortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'currentMargin': {
          const aVal = a.currentMargin?.marginAmount ?? a.currentMargin?.marginPercent ?? -Infinity;
          const bVal = b.currentMargin?.marginAmount ?? b.currentMargin?.marginPercent ?? -Infinity;
          cmp = aVal - bVal;
          break;
        }
        case 'activeTanks':
          cmp = a.activeTanks - b.activeTanks;
          break;
        case 'orderCount':
          cmp = a.orderCount - b.orderCount;
          break;
        case 'lastDeliveryDate': {
          const aDate = a.lastDeliveryDate ? new Date(a.lastDeliveryDate).getTime() : 0;
          const bDate = b.lastDeliveryDate ? new Date(b.lastDeliveryDate).getTime() : 0;
          cmp = aDate - bDate;
          break;
        }
        case 'revenue':
          cmp = a.revenue - b.revenue;
          break;
      }
      return locationSortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [locationSummaries, locationSortKey, locationSortAsc]);

  const handleLocationSort = (key: LocationSortKey) => {
    if (locationSortKey === key) {
      setLocationSortAsc(!locationSortAsc);
    } else {
      setLocationSortKey(key);
      setLocationSortAsc(key === 'name');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading customer data...
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-error-red via-steel to-error-red" />
          <div className="border-l-4 border-error-red pl-6">
            <h1 className="text-4xl font-display font-bold text-error-red tracking-wide uppercase">
              Customer Not Found
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <Link
                to="/customers"
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
              >
                &larr; Back to Customers
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6 relative">
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              Edit Customer
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <button
                onClick={() => setIsEditing(false)}
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
              >
                &larr; Cancel Editing
              </button>
            </div>
          </div>
        </div>

        <CustomerForm
          customerId={id}
          initialData={{
            name: customer.name,
            displayName: customer.displayName || '',
            customerNumber: customer.customerNumber,
            type: customer.type as 'COMMERCIAL' | 'RESIDENTIAL' | 'GOVERNMENT' | 'INDUSTRIAL',
            status: customer.status as 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'LEAD',
            primaryContactFirstName: customer.primaryContactFirstName || '',
            primaryContactLastName: customer.primaryContactLastName || '',
            email: customer.email || '',
            phone: customer.phone || '',
            fax: customer.fax || '',
            website: customer.website || '',
            billingAddress: customer.billingAddress || '',
            billingCity: customer.billingCity || '',
            billingState: customer.billingState || '',
            billingZip: customer.billingZip || '',
            deliveryAddress: customer.deliveryAddress || '',
            deliveryCity: customer.deliveryCity || '',
            deliveryState: customer.deliveryState || '',
            deliveryZip: customer.deliveryZip || '',
            taxId: customer.taxId || '',
            industryType: customer.industryType || '',
            creditLimit: customer.creditLimit?.toString() || '',
            creditTerms: customer.creditTerms?.toString() || '30',
            pricingTier: customer.pricingTier || 'STANDARD',
            paymentMethod: customer.paymentMethod || 'INVOICE',
            notes: customer.notes || '',
          }}
          onSuccess={() => {
            setIsEditing(false);
            fetchCustomer();
          }}
        />
      </div>
    );
  }

  // View mode - show details
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {customer.name}
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/customers"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Customers
                </Link>
                <span className="text-text-tertiary text-xs">|</span>
                <span className="text-text-tertiary text-sm font-mono">
                  {customer.customerNumber}
                </span>
                <span
                  className={`${getStatusColor(customer.status)} text-xs font-mono uppercase tracking-wider font-semibold`}
                  style={{ textShadow: '0 0 6px currentColor' }}
                >
                  {customer.status}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-steel/20 hover:bg-steel/30 text-steel font-mono uppercase text-sm tracking-wider transition-all border border-steel/30 hover:border-steel"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-6 py-3 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono uppercase text-sm tracking-wider transition-all border border-error-red/30 hover:border-error-red"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link
          to={`/orders/new?customerId=${customer.id}`}
          className="bg-amber hover:bg-amber/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm flex items-center space-x-2"
        >
          <span>+</span>
          <span>New Order</span>
        </Link>
        <Link
          to={`/invoices/new?customerId=${customer.id}`}
          className="bg-steel hover:bg-steel/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm flex items-center space-x-2"
        >
          <span>+</span>
          <span>New Invoice</span>
        </Link>
        <Link
          to={`/customers/${customer.id}/locations/new`}
          className="bg-panel-dark border border-panel-border hover:border-amber/50 text-text-secondary hover:text-text-primary font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm flex items-center space-x-2"
        >
          <span>+</span>
          <span>New Location</span>
        </Link>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <StatCard label="Locations" value={locations.length.toString()} />
        <StatCard label="Tanks" value={tanks.length.toString()} />
        <StatCard label="Total Orders" value={orders.length.toString()} />
        <StatCard label="Total Revenue" value={formatCurrency(totalRevenue)} />
        <StatCard
          label="Outstanding"
          value={formatCurrency(outstandingBalance)}
          status={outstandingBalance > 0 ? 'warning' : 'operational'}
        />
        <StatCard
          label="Credit Limit"
          value={customer.creditLimit ? formatCurrency(customer.creditLimit) : 'None'}
        />
      </div>

      {/* Main Tabs Navigation */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          {/* Tab Headers */}
          <div className="flex border-b border-panel-border mb-6 overflow-x-auto">
            {(['overview', 'locations', 'tanks', 'margins', 'orders', 'invoices'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-6 py-3 font-mono uppercase text-sm tracking-wider transition-all border-b-2 -mb-[2px] whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-amber text-amber'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab === 'overview' && 'Overview'}
                {tab === 'locations' && `Locations (${locations.length})`}
                {tab === 'tanks' && `Tanks (${tanks.length})`}
                {tab === 'margins' && 'Margins'}
                {tab === 'orders' && `Orders (${orders.length})`}
                {tab === 'invoices' && `Invoices (${invoices.length})`}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Info */}
              <InfoPanel title="Basic Information">
                <InfoRow label="Company Name" value={customer.name} />
                <InfoRow label="Display Name" value={customer.displayName} />
                <InfoRow label="Customer Number" value={customer.customerNumber} />
                <InfoRow label="Type" value={customer.type} />
                <InfoRow label="Status" value={customer.status} />
                <InfoRow label="Industry" value={customer.industryType} />
              </InfoPanel>

              {/* Contact Info */}
              <InfoPanel title="Contact Information">
                {/* Rendered from the two stored fields, never from a stored
                    joined string — a denormalised copy would drift the first
                    time a surname was corrected. */}
                <InfoRow
                  label="Primary Contact"
                  value={fullName({
                    firstName: customer.primaryContactFirstName,
                    lastName: customer.primaryContactLastName,
                  })}
                />
                <InfoRow label="Email" value={customer.email} />
                <InfoRow label="Phone" value={customer.phone} />
                <InfoRow label="Fax" value={customer.fax} />
                <InfoRow label="Website" value={customer.website} />
              </InfoPanel>

              {/* Billing Address */}
              <InfoPanel title="Billing Address">
                <InfoRow label="Street" value={customer.billingAddress} />
                <InfoRow label="City" value={customer.billingCity} />
                <InfoRow label="State" value={customer.billingState} />
                <InfoRow label="ZIP Code" value={customer.billingZip} />
              </InfoPanel>

              {/* Business & Credit - hidden if accounting fields stripped by server */}
              <InfoPanel title="Business & Credit">
                <InfoRow label="Tax ID" value={customer.taxId} />
                {'creditLimit' in customer && (
                  <>
                    <InfoRow
                      label="Credit Limit"
                      value={
                        customer.creditLimit ? `$${Number(customer.creditLimit).toLocaleString()}` : null
                      }
                      highlight
                    />
                    <InfoRow
                      label="Credit Terms"
                      value={customer.creditTerms ? `Net ${customer.creditTerms}` : null}
                    />
                  </>
                )}
                <InfoRow label="Pricing Tier" value={customer.pricingTier} />
                <InfoRow label="Payment Method" value={customer.paymentMethod} />
              </InfoPanel>
            </div>
          )}

          {/* Locations Tab - Enhanced Summary Table */}
          {activeTab === 'locations' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-text-tertiary font-mono text-sm">
                  Ship-to locations &mdash; 90-day performance summary
                </p>
                <Link
                  to={`/customers/${customer.id}/locations/new`}
                  className="bg-amber hover:bg-amber/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm"
                >
                  + New Location
                </Link>
              </div>

              {sortedLocationSummaries.length === 0 ? (
                <div className="text-center py-8 text-text-tertiary font-mono">
                  No locations found
                </div>
              ) : (
                <div className="relative">
                  {/* Corner brackets */}
                  <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber z-10" />
                  <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber z-10" />
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber z-10" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber z-10" />

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse" style={{ background: '#0a0e14' }}>
                      <thead>
                        <tr className="border-b-2 border-amber/40">
                          {([
                            { key: 'name' as LocationSortKey, label: 'Location' },
                            { key: 'currentMargin' as LocationSortKey, label: 'Margin' },
                            { key: 'activeTanks' as LocationSortKey, label: 'Tanks' },
                            { key: 'orderCount' as LocationSortKey, label: 'Orders (90d)' },
                            { key: 'lastDeliveryDate' as LocationSortKey, label: 'Last Delivered' },
                            { key: 'revenue' as LocationSortKey, label: 'Revenue (90d)' },
                          ]).map((col) => (
                            <th
                              key={col.key}
                              onClick={() => handleLocationSort(col.key)}
                              className="px-4 py-3 text-left text-xs font-mono uppercase tracking-widest text-amber cursor-pointer select-none hover:text-amber/80 transition-colors whitespace-nowrap"
                            >
                              <span className="inline-flex items-center gap-1">
                                {col.label}
                                {locationSortKey === col.key && (
                                  <span className="text-amber/60">
                                    {locationSortAsc ? '\u25B2' : '\u25BC'}
                                  </span>
                                )}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLocationSummaries.map((loc, idx) => (
                          <tr
                            key={loc.id}
                            onClick={() => navigate(`/customers/${customer.id}/locations/${loc.id}`)}
                            className={`border-b border-panel-border/50 cursor-pointer transition-all hover:bg-steel/10 ${
                              idx % 2 === 0 ? 'bg-[#0a0e14]' : 'bg-[#0d1219]'
                            } ${!loc.isActive ? 'opacity-50' : ''}`}
                          >
                            {/* Location Name */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm text-text-primary font-semibold">
                                  {loc.name}
                                </span>
                                {loc.locationCode && (
                                  <span className="font-mono text-xs text-text-tertiary">
                                    [{loc.locationCode}]
                                  </span>
                                )}
                                {!loc.isActive && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-mono bg-gray-500/20 text-gray-400 border border-gray-500/50 uppercase">
                                    off
                                  </span>
                                )}
                              </div>
                              {(loc.city || loc.state) && (
                                <div className="font-mono text-xs text-text-tertiary mt-0.5">
                                  {loc.city}{loc.city && loc.state ? ', ' : ''}{loc.state}
                                </div>
                              )}
                            </td>

                            {/* Current Margin */}
                            <td className="px-4 py-3 font-mono text-sm text-right whitespace-nowrap">
                              {loc.currentMargin ? (
                                <span className="text-text-primary">
                                  {loc.currentMargin.marginAmount != null
                                    ? `$${loc.currentMargin.marginAmount.toFixed(4)}/gal`
                                    : loc.currentMargin.marginPercent != null
                                    ? `${loc.currentMargin.marginPercent.toFixed(2)}%`
                                    : '-'}
                                </span>
                              ) : (
                                <span className="text-text-tertiary">-</span>
                              )}
                            </td>

                            {/* Active Tanks */}
                            <td className="px-4 py-3 font-mono text-sm text-right">
                              <span className={loc.activeTanks > 0 ? 'text-steel' : 'text-text-tertiary'}>
                                {loc.activeTanks}
                              </span>
                            </td>

                            {/* Orders (90d) */}
                            <td className="px-4 py-3 font-mono text-sm text-right">
                              <span className={loc.orderCount > 0 ? 'text-text-primary' : 'text-text-tertiary'}>
                                {loc.orderCount}
                              </span>
                            </td>

                            {/* Last Delivered */}
                            <td className="px-4 py-3 font-mono text-sm text-right whitespace-nowrap">
                              {loc.lastDeliveryDate ? (
                                <span className="text-text-secondary">
                                  {formatDate(loc.lastDeliveryDate)}
                                </span>
                              ) : (
                                <span className="text-text-tertiary">-</span>
                              )}
                            </td>

                            {/* Revenue (90d) */}
                            <td className="px-4 py-3 font-mono text-sm text-right whitespace-nowrap">
                              <span
                                className={loc.revenue > 0 ? 'text-amber font-semibold' : 'text-text-tertiary'}
                                style={loc.revenue > 0 ? { textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' } : undefined}
                              >
                                {loc.revenue > 0 ? formatCurrency(loc.revenue) : '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>

                      {/* Totals Row */}
                      {locationTotals && (
                        <tfoot>
                          <tr className="border-t-2 border-amber/40" style={{ background: '#080c12' }}>
                            <td className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-amber font-semibold">
                              Totals ({locationTotals.activeLocations} active / {locationTotals.totalLocations})
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-right text-text-tertiary">
                              -
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-right text-steel font-semibold">
                              {locationTotals.totalTanks}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-right text-text-primary font-semibold">
                              {locationTotals.totalOrders}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-right text-text-tertiary">
                              -
                            </td>
                            <td
                              className="px-4 py-3 font-mono text-sm text-right text-amber font-bold"
                              style={{ textShadow: '0 0 8px rgba(255, 149, 0, 0.5)' }}
                            >
                              {formatCurrency(locationTotals.totalRevenue)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tanks Tab */}
          {activeTab === 'tanks' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-text-tertiary font-mono text-sm">
                  Tanks assigned to this customer's locations
                </p>
                <Link
                  to={`/tanks/new?customerId=${customer.id}`}
                  className="bg-amber hover:bg-amber/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm"
                >
                  + New Tank
                </Link>
              </div>
              {tanks.length === 0 ? (
                <div className="text-center py-8 text-text-tertiary font-mono">
                  No tanks assigned to this customer
                </div>
              ) : (
                <div className="space-y-3">
                  {tanks.map((tank) => {
                    const fillPercent = tank.capacity > 0 ? (tank.currentLevel / tank.capacity) * 100 : 0;
                    const fillColor = fillPercent < 25 ? 'bg-error-red' : fillPercent < 50 ? 'bg-amber' : 'bg-success-green';

                    return (
                      <Link
                        key={tank.id}
                        to={`/tanks/${tank.id}`}
                        className="block p-4 bg-panel-border/30 hover:bg-panel-border/50 transition-all border border-panel-border hover:border-steel/50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-steel font-mono font-semibold">
                                {tank.tankNumber}
                              </span>
                              <span className="text-text-secondary font-mono">
                                {tank.name}
                              </span>
                            </div>
                            <div className="text-text-tertiary font-mono text-sm mt-1">
                              {tank.product?.name || 'No product'}
                              {tank.customerLocation && (
                                <> • {tank.customerLocation.name}</>
                              )}
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 text-xs font-mono ${
                              tank.status === 'ACTIVE'
                                ? 'bg-success-green/20 text-success-green border border-success-green'
                                : 'bg-gray-500/20 text-gray-400 border border-gray-500'
                            }`}
                          >
                            {tank.status}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs font-mono text-text-tertiary">
                            <span>
                              {tank.currentLevel.toLocaleString()} / {tank.capacity.toLocaleString()} gal
                            </span>
                            <span>{Math.round(fillPercent)}%</span>
                          </div>
                          <div className="h-2 bg-background border border-panel-border">
                            <div
                              className={`h-full ${fillColor} transition-all`}
                              style={{ width: `${fillPercent}%` }}
                            />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Margins Tab */}
          {activeTab === 'margins' && (
            <CustomerMargins customerId={customer.id} />
          )}

          {/* Orders Tab Content */}
          {activeTab === 'orders' && (
            <div className="space-y-3">
              {orders.length === 0 ? (
                <div className="text-text-tertiary font-mono text-sm py-8 text-center">
                  No orders found for this customer
                </div>
              ) : (
                orders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className="block p-4 bg-panel-border/30 hover:bg-panel-border/50 transition-all border border-panel-border hover:border-steel/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-steel font-mono font-semibold">
                          {order.orderNumber}
                        </span>
                        <span className="text-text-tertiary text-xs font-mono uppercase">
                          {order.orderType}
                        </span>
                        <span
                          className={`text-xs font-mono uppercase tracking-wider ${getOrderStatusColor(order.status)}`}
                        >
                          {order.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-6">
                        <span className="text-text-secondary font-mono text-sm">
                          {formatDate(order.orderDate)}
                        </span>
                        <span className="text-amber font-mono font-semibold">
                          {formatCurrency(order.total)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}

          {/* Invoices Tab Content */}
          {activeTab === 'invoices' && (
            <div className="space-y-3">
              {invoices.length === 0 ? (
                <div className="text-text-tertiary font-mono text-sm py-8 text-center">
                  No invoices found for this customer
                </div>
              ) : (
                invoices.map((invoice) => (
                  <Link
                    key={invoice.id}
                    to={`/invoices/${invoice.id}`}
                    className="block p-4 bg-panel-border/30 hover:bg-panel-border/50 transition-all border border-panel-border hover:border-steel/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-steel font-mono font-semibold">
                          {invoice.invoiceNumber}
                        </span>
                        <span
                          className={`text-xs font-mono uppercase tracking-wider ${getInvoiceStatusColor(invoice.status)}`}
                        >
                          {invoice.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-6">
                        <span className="text-text-secondary font-mono text-sm">
                          {formatDate(invoice.invoiceDate)}
                        </span>
                        <span className="text-text-tertiary font-mono text-xs">
                          Due: {formatDate(invoice.dueDate)}
                        </span>
                        <span className="text-amber font-mono font-semibold">
                          {formatCurrency(invoice.amountDue || invoice.total)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Timestamps */}
      <div className="text-text-tertiary text-xs font-mono flex items-center space-x-6">
        <span>Created: {new Date(customer.createdAt).toLocaleString()}</span>
        <span>Updated: {new Date(customer.updatedAt).toLocaleString()}</span>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-error-red mb-4 uppercase">
                Confirm Delete
              </h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to delete <strong>{customer.name}</strong>? This action
                cannot be undone.
              </p>

              {deleteError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {deleteError}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError('');
                  }}
                  className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 bg-error-red hover:bg-error-red/90 text-white font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Delete Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Info panel with geometric styling
 */
function InfoPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Info row for displaying label/value pairs
 */
function InfoRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | null | undefined;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between items-center border-b border-panel-border pb-3 last:border-0 last:pb-0">
      <span className="text-text-secondary text-xs uppercase tracking-widest font-mono">
        {label}
      </span>
      <span
        className={`font-mono ${highlight ? 'text-amber font-semibold' : 'text-text-primary'}`}
        style={highlight ? { textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' } : undefined}
      >
        {value || <span className="text-text-tertiary">-</span>}
      </span>
    </div>
  );
}

/**
 * Stat card for displaying metrics
 */
function StatCard({
  label,
  value,
  status = 'operational',
}: {
  label: string;
  value: string;
  status?: 'operational' | 'warning' | 'critical';
}) {
  const statusColors = {
    operational: 'text-steel border-steel/30',
    warning: 'text-amber border-amber/30',
    critical: 'text-error-red border-error-red/30',
  };

  return (
    <div
      className={`relative bg-panel-dark border p-4 ${statusColors[status]}`}
      style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-current opacity-50" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-current opacity-50" />
      <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-1">
        {label}
      </div>
      <div
        className="text-xl font-display font-bold"
        style={{ textShadow: '0 0 10px currentColor' }}
      >
        {value}
      </div>
    </div>
  );
}

export default CustomerDetailPage;
